// Per-user self-service: rotate own password. Available to every logged-in
// user regardless of role. Lives outside the per-VPS area — it's about the
// UI-host identity, not the engine.

import { useState } from "react";
import type { FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { auth } from "../api/auth";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

export function AccountPage() {
  const { username, role } = useAuth();

  return (
    <div className="app-main">
      <h2>Account</h2>
      <div className="card">
        <div className="row wrap">
          <div>
            <div className="stat-label">Username</div>
            <div className="mono">{username}</div>
          </div>
          <div>
            <div className="stat-label">Role</div>
            <div>
              <span className={role === "admin" ? "pill ghost" : "pill dim"}>{role}</span>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordForm />
    </div>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const change = useMutation({
    mutationFn: () => auth.changeMyPassword(current, next),
    onSuccess: () => {
      setOk(true);
      setErr(null);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : "change failed");
      setOk(false);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (next !== confirm) {
      setErr("new passwords do not match");
      return;
    }
    if (next.length < 8) {
      setErr("new password must be at least 8 characters");
      return;
    }
    change.mutate();
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2>Change password</h2>
      {ok && <div className="info-banner">Password changed.</div>}
      {err && <div className="error-banner">{err}</div>}
      <div className="form-fields" style={{ gap: 12 }}>
        <label>
          <span className="label-text">Current password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        <label>
          <span className="label-text">New password</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
          />
        </label>
        <label>
          <span className="label-text">Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </label>
      </div>
      <button
        className="primary"
        type="submit"
        style={{ marginTop: 12 }}
        disabled={change.isPending || !current || !next || !confirm}
      >
        {change.isPending ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}
